import { useCallback, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useOrganization } from '@/hooks/useOrganization';

const TIMEZONES = [
  { value: 'America/Toronto', label: 'Eastern Time (Toronto)' },
  { value: 'America/Vancouver', label: 'Pacific Time (Vancouver)' },
  { value: 'America/Chicago', label: 'Central Time (Chicago)' },
  { value: 'America/Denver', label: 'Mountain Time (Denver)' },
  { value: 'America/New_York', label: 'Eastern Time (New York)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
  { value: 'UTC', label: 'UTC' },
];

const CURRENCIES = [
  { value: 'CAD', label: 'CAD - Canadian Dollar' },
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'EUR', label: 'EUR - Euro' },
  { value: 'GBP', label: 'GBP - British Pound' },
];

export function OrganizationSettings() {
  const { id } = useParams<{ id: string }>();
  const orgId = Number(id);
  const navigate = useNavigate();
  const { organizations, switchOrganization, createOrganization, refreshOrganizations } =
    useOrganization();

  const [createOpen, setCreateOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [currency, setCurrency] = useState('CAD');
  const [timezone, setTimezone] = useState('America/Toronto');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const handleCreate = useCallback(async () => {
    setCreateError(null);
    if (!newOrgName.trim()) {
      setCreateError('Organization name is required.');
      return;
    }
    setCreateLoading(true);
    try {
      const { error } = await createOrganization({
        name: newOrgName.trim(),
        currency,
        timezone,
      });

      if (error) {
        setCreateError(error.message);
        return;
      }

      await refreshOrganizations();
      setCreateOpen(false);
      setNewOrgName('');
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create organization');
    } finally {
      setCreateLoading(false);
    }
  }, [newOrgName, currency, timezone, createOrganization, refreshOrganizations]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pb-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Organization Settings</h1>
          <p className="text-sm text-muted-foreground">
            Manage your organization profile, members, and tax settings.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {organizations.length > 1 && (
            <Select
              value={String(orgId)}
              onValueChange={(v) => {
                switchOrganization(Number(v));
                void navigate(`/app/organizations/${v}/settings/members`);
              }}
            >
              <SelectTrigger size="sm" className="w-48">
                {organizations.find((o) => o.id === orgId)?.name ?? 'Select Org'}
              </SelectTrigger>
              <SelectContent>
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={String(org.id)}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}>
            <i className="bi bi-plus-lg mr-1"></i> Create Organization
          </Button>
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <Outlet />
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Organization</DialogTitle>
            <DialogDescription>
              Create an additional organization account. You will become the owner of this new organization.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            {createError && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
                {createError}
              </div>
            )}

            <div>
              <label htmlFor="new-org-name" className="block text-sm font-medium text-foreground mb-1">
                Organization Name *
              </label>
              <input
                id="new-org-name"
                type="text"
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                placeholder="My New Company"
                autoFocus
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleCreate();
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Default Currency
              </label>
              <Select value={currency} onValueChange={(v) => { if (v) setCurrency(v) }}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Timezone
              </label>
              <Select value={timezone} onValueChange={(v) => { if (v) setTimezone(v) }}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={createLoading}>
              Cancel
            </Button>
            <Button onClick={() => void handleCreate()} disabled={createLoading || !newOrgName.trim()}>
              {createLoading ? 'Creating...' : 'Create Organization'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
